import React from 'react';

// This must be a class component at the moment.

/**
 * The properties of the fallback component.
 */
export interface IFallbackComponentProps {
  /**
   * The handled error.
   */
  error: Error;
  /**
   * The information about the error.
   */
  errorInfo: React.ErrorInfo;
  /**
   * A function to reset the error state.
   */
  resetErrorBoundary(): void;
}

/**
 * The properties of the error boundary component.
 */
export interface IErrorBoundaryProps {
  /**
   * A component to show in case of errors.
   */
  FallbackComponent: React.ComponentType<IFallbackComponentProps>;
  /**
   * A function that is called immediately before the error boundary is reset.
   * Use it to change the state so that the same error will not happen upon reset.
   */
  onReset?(): void;
  /**
   * A function that is called when an error occurs.
   * Use it to perform some addidional actions upon an error.
   */
  onError?(error: Error, errorInfo: React.ErrorInfo): void;
}

/**
 * The state of the error boundary component.
 */
interface IErrorBoundaryState {
  /**
   * The current error.
   */
  error: [Error , React.ErrorInfo] | null;
}

/**
 * A component that handles errors and shows them in a provided component.
 */
export default class extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);

    this.state = {
      error: null,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({
      error: [error, info],
    });
    this.props.onError?.(error, info);
  }

  render() {
    const { error } = this.state;
    const { FallbackComponent, children } = this.props;

    if (error != null) {
      return (
        <FallbackComponent
          error={error[0]}
          errorInfo={error[1]}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }

    return children;
  }

  private resetErrorBoundary(): void {
    this.props.onReset?.();
    this.setState({
      error: null,
    });
  }
}
